<template>
  <div
    class="stat-card"
    :class="{ 'stat-card--clickable': clickable, 'is-active': active }"
    :role="clickable ? 'button' : null"
    :tabindex="clickable ? 0 : null"
    :aria-pressed="clickable ? (active ? 'true' : 'false') : null"
    @click="clickable && $emit('click', $event)"
    @keydown.enter.prevent="clickable && $emit('click', $event)"
  >
    <div v-if="icon" class="stat-card__icon" :class="`stat-card__icon--${tone}`">
      <i :class="icon"></i>
    </div>
    <div class="stat-card__text">
      <div class="stat-card__value"><slot name="value">{{ display }}</slot></div>
      <div class="stat-card__label">{{ label }}</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StatCard',
  props: {
    label: { type: String, default: '' },
    value: { type: [String, Number], default: '' },
    icon: { type: String, default: '' },
    tone: { type: String, default: 'brand' }, // brand | success | warning | danger | violet
    // по карточке включён фильтр таблицы
    active: { type: Boolean, default: false }
  },
  data() {
    return { shown: null, frame: 0, timer: 0 }
  },
  computed: {
    // Карточка нажимается, если на неё подписаны (@click)
    clickable() {
      return !!(this.$listeners && this.$listeners.click)
    },
    // Целое число «набегает» от прежнего значения к новому; всё остальное («2 ч 35 мин», «—») показывается как есть
    display() {
      return this.shown !== null ? this.shown : this.value
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(to, from) {
        this.count(to, from)
      }
    }
  },
  beforeDestroy() {
    cancelAnimationFrame(this.frame)
    clearTimeout(this.timer)
  },
  methods: {
    isCount(v) {
      return (typeof v === 'number' && Number.isInteger(v) && v >= 0) || (typeof v === 'string' && /^\d{1,9}$/.test(v))
    },
    count(to, from) {
      cancelAnimationFrame(this.frame)
      clearTimeout(this.timer)
      const reduce = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      // в скрытой вкладке кадры анимации не идут: сразу показываем итог
      if (!this.isCount(to) || reduce || document.hidden) {
        this.shown = null
        return
      }
      const end = Number(to)
      const start = this.isCount(from) ? Number(from) : 0
      if (start === end) {
        this.shown = null
        return
      }
      const began = performance.now()
      const span = this.isCount(from) ? 350 : 650
      const step = (now) => {
        const t = Math.min(1, (now - began) / span)
        const eased = 1 - Math.pow(1 - t, 3)
        this.shown = Math.round(start + (end - start) * eased)
        if (t < 1) this.frame = requestAnimationFrame(step)
        else this.shown = null
      }
      this.shown = start
      this.frame = requestAnimationFrame(step)
      // если кадры остановились посреди анимации (вкладку скрыли), итоговое число всё равно появится
      this.timer = setTimeout(() => {
        cancelAnimationFrame(this.frame)
        this.shown = null
      }, span + 250)
    }
  }
}
</script>

<style scoped>
.stat-card {
  background: var(--bg-surface);
  border-radius: var(--radius-lg);
  padding: var(--space-8) var(--space-10);
  border: var(--border-width-default) var(--border-style-base) var(--border-default);
  box-shadow: var(--shadow-xs);
  display: flex;
  align-items: center;
  gap: var(--space-10);
  transition: border-color var(--duration-normal) var(--ease-base), box-shadow var(--duration-normal) var(--ease-out), transform var(--duration-normal) var(--ease-out), background var(--duration-normal) var(--ease-base);
}

.stat-card__icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-pill);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-md);
}
.stat-card__icon--brand   { background: var(--primary-soft); color: var(--primary); }
.stat-card__icon--success { background: var(--success-soft); color: var(--success); }
.stat-card__icon--warning { background: var(--warning-soft); color: var(--warning-text); }
.stat-card__icon--danger  { background: var(--danger-soft); color: var(--danger); }
.stat-card__icon--violet  { background: var(--violet-soft); color: var(--violet); }

.stat-card__value {
  font-size: var(--text-lg);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  line-height: var(--line-height-tight);
}

.stat-card__label {
  font-size: var(--text-label);
  line-height: 1.25;
  color: var(--text-secondary);
  margin-top: var(--space-2);
}

/* На телефоне карточки показателей компактнее: по две в ряд */
@media (max-width: 640px) {
  .stat-card { padding: var(--space-6) var(--space-8); gap: var(--space-6); }
  .stat-card__icon { width: 34px; height: 34px; font-size: var(--text-base); flex-shrink: 0; }
  .stat-card__value { font-size: var(--text-md); }
}

.stat-card--clickable {
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
/* Наведение — только там, где есть мышь (на сенсорном экране :hover остаётся после касания).
   Ничего не рисуется за границей карточки и не сдвигается вверх: блоки страницы и карусель обрезают всё, что выходит за край. */
@media (hover: hover) and (pointer: fine) {
  .stat-card--clickable:hover {
    border-color: var(--primary);
    box-shadow: var(--shadow-sm);
  }
}
.stat-card--clickable:active { transform: scale(0.98); }
.stat-card--clickable:focus-visible { outline: 2px solid var(--primary); outline-offset: -2px; }
/* Выбранная карточка: рамка толще внутрь, а не наружу (снаружи её обрезало бы) */
.stat-card.is-active {
  border-color: var(--primary);
  background: var(--primary-soft-2);
  box-shadow: inset 0 0 0 1px var(--primary);
}
</style>
